/**
 * SecurityPatrols - Patrol rounds management with checkpoint photos
 * For security officers to log their patrol walks
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  MapPin, Camera, Play, Square, Check, Clock, Route, 
  Plus, Image, ChevronRight, AlertCircle, Navigation,
  CheckCircle2, Circle, Footprints, History, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import LocalCameraCapture from './LocalCameraCapture';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityPatrols = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('patrol');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');

  const token = getToken() || localStorage.getItem('security_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch active patrol
  const { data: activePatrolData, isLoading: patrolLoading } = useQuery({
    queryKey: ['active-patrol'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/patrols/active`, { headers });
      if (!res.ok) return { active_patrol: null };
      return res.json();
    },
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  const activePatrol = activePatrolData?.active_patrol;

  // Fetch patrol routes
  const { data: routes = [] } = useQuery({
    queryKey: ['patrol-routes'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/patrol-routes`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch checkpoints
  const { data: allCheckpoints = [] } = useQuery({
    queryKey: ['checkpoints'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/checkpoints`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch patrol history
  const { data: patrolHistory = [] } = useQuery({
    queryKey: ['patrol-history'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/patrols/history`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Start patrol mutation
  const startPatrolMutation = useMutation({
    mutationFn: async (routeId) => {
      const res = await fetch(`${API_URL}/api/patrols/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ route_id: routeId })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to start patrol');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['active-patrol']);
      setShowStartDialog(false);
      toast.success('Patrol started!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/patrols/${activePatrol.id}/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Check-in failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['active-patrol']);
      setShowCheckInDialog(false);
      setSelectedCheckpoint(null);
      setCapturedPhoto(null);
      setCheckInNotes('');
      toast.success(data.message);
    },
    onError: (error) => toast.error(error.message)
  });

  // Complete patrol mutation
  const completePatrolMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/patrols/${activePatrol.id}/complete`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Failed to complete patrol');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['active-patrol']);
      queryClient.invalidateQueries(['patrol-history']);
      toast.success('Patrol completed!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Cancel patrol mutation
  const cancelPatrolMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/patrols/${activePatrol.id}/cancel`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Failed to cancel patrol');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['active-patrol']);
      toast.success('Patrol cancelled');
    },
    onError: (error) => toast.error(error.message)
  });

  // Handle camera capture
  const handleCameraCapture = useCallback((imageData) => {
    setCapturedPhoto(imageData);
    setShowCameraCapture(false);
  }, []);

  const handleCheckIn = (checkpoint) => {
    setSelectedCheckpoint(checkpoint);
    setShowCheckInDialog(true);
  };

  const submitCheckIn = () => {
    if (!capturedPhoto && selectedCheckpoint?.required_photo) {
      toast.error('Photo is required for this checkpoint');
      return;
    }
    
    checkInMutation.mutate({
      checkpoint_id: selectedCheckpoint.id,
      photo_base64: capturedPhoto,
      notes: checkInNotes
    });
  };

  // Get checkpoints for active patrol
  const patrolCheckpoints = activePatrol?.checkpoints || allCheckpoints;
  const completedCheckpointIds = (activePatrol?.check_ins || []).map(ci => ci.checkpoint_id);
  const progress = activePatrol ? (activePatrol.completed_checkpoints / activePatrol.total_checkpoints) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="security-patrols">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="patrol" className="flex items-center gap-2">
            <Footprints className="h-4 w-4" />
            <span className="hidden sm:inline">Active Patrol</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
          <TabsTrigger value="checkpoints" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">Checkpoints</span>
          </TabsTrigger>
        </TabsList>

        {/* Active Patrol Tab */}
        <TabsContent value="patrol" className="space-y-4 mt-4">
          {patrolLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : !activePatrol ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Footprints className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No Active Patrol</h3>
                <p className="text-sm text-gray-500 mb-6">Start a patrol to begin logging your rounds</p>
                <Button onClick={() => setShowStartDialog(true)} size="lg" data-testid="start-patrol-btn">
                  <Play className="h-5 w-5 mr-2" />
                  Start Patrol
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Active Patrol Header */}
              <Card className="bg-gradient-to-r from-green-600 to-emerald-700 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-3 w-3 rounded-full bg-white animate-pulse" />
                        <span className="text-sm font-medium">Patrol In Progress</span>
                      </div>
                      <h3 className="text-xl font-bold">{activePatrol.route_name}</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-green-100 text-sm">
                        <Clock className="h-4 w-4 inline mr-1" />
                        Started {new Date(activePatrol.started_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{activePatrol.completed_checkpoints} / {activePatrol.total_checkpoints} checkpoints</span>
                    </div>
                    <Progress value={progress} className="h-3 bg-green-800" />
                  </div>
                </CardContent>
              </Card>

              {/* Checkpoints List */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Checkpoints</h4>
                {patrolCheckpoints.map((checkpoint, index) => {
                  const isCompleted = completedCheckpointIds.includes(checkpoint.id);
                  const checkIn = activePatrol?.check_ins?.find(ci => ci.checkpoint_id === checkpoint.id);
                  
                  return (
                    <Card 
                      key={checkpoint.id} 
                      className={`transition-all ${isCompleted ? 'bg-green-50 border-green-200' : 'hover:shadow-md'}`}
                      data-testid={`checkpoint-${checkpoint.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <span>{index + 1}</span>}
                            </div>
                            <div>
                              <p className="font-medium">{checkpoint.name}</p>
                              <p className="text-sm text-gray-500">{checkpoint.location}</p>
                              {isCompleted && checkIn && (
                                <p className="text-xs text-green-600 mt-1">
                                  ✓ Checked in at {new Date(checkIn.timestamp).toLocaleTimeString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {checkpoint.required_photo && !isCompleted && (
                              <Badge variant="outline" className="text-xs">
                                <Camera className="h-3 w-3 mr-1" />
                                Photo required
                              </Badge>
                            )}
                            {!isCompleted && (
                              <Button 
                                onClick={() => handleCheckIn(checkpoint)}
                                data-testid={`checkin-${checkpoint.id}`}
                              >
                                <Camera className="h-4 w-4 mr-2" />
                                Check In
                              </Button>
                            )}
                            {isCompleted && checkIn?.photo_url && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => window.open(`${API_URL}${checkIn.photo_url}`, '_blank')}
                              >
                                <Image className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Patrol Actions */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => cancelPatrolMutation.mutate()}
                  disabled={cancelPatrolMutation.isPending}
                >
                  <Square className="h-4 w-4 mr-2" />
                  Cancel Patrol
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => completePatrolMutation.mutate()}
                  disabled={completePatrolMutation.isPending || activePatrol.completed_checkpoints === 0}
                  data-testid="complete-patrol-btn"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Complete Patrol
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <h4 className="font-medium text-gray-700">Recent Patrols</h4>
          {patrolHistory.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No patrol history yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {patrolHistory.map((patrol) => (
                <Card key={patrol.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{patrol.route_name}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(patrol.started_at).toLocaleDateString()} at {new Date(patrol.started_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          patrol.status === 'completed' ? 'bg-green-100 text-green-800' :
                          patrol.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {patrol.status}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {patrol.completed_checkpoints}/{patrol.total_checkpoints} checkpoints
                        </p>
                        {patrol.duration_minutes && (
                          <p className="text-xs text-gray-400">
                            {patrol.duration_minutes} min
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Checkpoints Tab */}
        <TabsContent value="checkpoints" className="space-y-4 mt-4">
          <h4 className="font-medium text-gray-700">All Checkpoints</h4>
          {allCheckpoints.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No checkpoints configured</p>
                <p className="text-sm">Ask your administrator to set up patrol checkpoints</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {allCheckpoints.map((checkpoint, index) => (
                <Card key={checkpoint.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{checkpoint.name}</p>
                        <p className="text-sm text-gray-500">{checkpoint.location}</p>
                        {checkpoint.description && (
                          <p className="text-xs text-gray-400 mt-1">{checkpoint.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{checkpoint.qr_code}</code>
                        {checkpoint.required_photo && (
                          <p className="text-xs text-orange-500 mt-1">Photo required</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Start Patrol Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Start Patrol</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Select a patrol route to begin:</p>
            {routes.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Route className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No routes configured</p>
                <Button 
                  className="mt-4"
                  onClick={() => startPatrolMutation.mutate(null)}
                  disabled={startPatrolMutation.isPending}
                >
                  Start Free Patrol
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {routes.map((route) => (
                  <Card 
                    key={route.id} 
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => startPatrolMutation.mutate(route.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{route.name}</p>
                          <p className="text-sm text-gray-500">
                            {route.checkpoints?.length || 0} checkpoints • ~{route.estimated_duration_minutes} min
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => startPatrolMutation.mutate(null)}
                  disabled={startPatrolMutation.isPending}
                >
                  Start Free Patrol (All Checkpoints)
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={(open) => {
        if (!open) {
          setCapturedPhoto(null);
          setCheckInNotes('');
          setSelectedCheckpoint(null);
        }
        setShowCheckInDialog(open);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Check In: {selectedCheckpoint?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{selectedCheckpoint?.location}</p>
            
            {/* Camera/Photo Section */}
            <div className="space-y-3">
              {capturedPhoto ? (
                <div className="relative">
                  <img src={capturedPhoto} alt="Captured" className="w-full rounded-lg" />
                  <Button 
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => setCapturedPhoto(null)}
                  >
                    Retake
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                  <Camera className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500 mb-4">Take a photo of this checkpoint</p>
                  <Button onClick={() => setShowCameraCapture(true)} data-testid="capture-patrol-photo">
                    <Camera className="h-4 w-4 mr-2" />
                    Open Camera
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                value={checkInNotes}
                onChange={(e) => setCheckInNotes(e.target.value)}
                placeholder="Any observations or issues..."
                rows={2}
              />
            </div>

            {/* Required photo warning */}
            {selectedCheckpoint?.required_photo && !capturedPhoto && (
              <div className="flex items-center gap-2 p-3 bg-orange-50 text-orange-700 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Photo is required for this checkpoint
              </div>
            )}

            {/* Submit */}
            <Button 
              onClick={submitCheckIn}
              disabled={checkInMutation.isPending || (selectedCheckpoint?.required_photo && !capturedPhoto)}
              className="w-full"
              data-testid="submit-checkin"
            >
              {checkInMutation.isPending ? 'Checking in...' : 'Confirm Check-in'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Local Camera Capture for Patrol Photos */}
      <LocalCameraCapture
        isOpen={showCameraCapture}
        onClose={() => setShowCameraCapture(false)}
        onCapture={handleCameraCapture}
        title="Checkpoint Photo"
        guideLine={`Capture: ${selectedCheckpoint?.name}`}
      />
    </div>
  );
};

export default SecurityPatrols;
